import isUndefined from 'lodash/isUndefined';
import mapValues from 'lodash/mapValues';
import isObject from 'lodash/isObject';
import omitBy from 'lodash/omitBy';

/**
 * Strips `undefined`-valued keys, recursively, and returns the same shape.
 *
 * The signature is `<T>(value: T): T` because that is what the body does — it
 * is shape-preserving, and it passes anything that is not a plain object
 * straight through. The previous
 * `<TKey extends string, TValue>(obj: Record<TKey, TValue>): Record<TKey, TValue>`
 * claimed something else: given a real object type, `TValue` infers as the
 * UNION of every property type and `Record<keyof T, thatUnion>` then demands
 * that EVERY key accept EVERY other key's type. So passing any actual DTO
 * failed, and two API call sites carried a `@ts-ignore` for it — while
 * `null`/primitives, which the guard below handles at runtime, the type
 * refused outright.
 *
 * Dropping an optional property is not a type change (`{ a?: number }` is
 * satisfied with or without `a`), so `T` in and `T` out is accurate.
 */
export function omitDeepUndefined<T>(value: T): T {
  if (!isObject(value) || Array.isArray(value) || value === null) {
    return value;
  }

  return omitBy(
    mapValues(value as Record<string, unknown>, (member) =>
      omitDeepUndefined(member),
    ),
    isUndefined,
  ) as T;
}
